import { useState } from 'react';
import { Layers } from 'lucide-react';
import { DataTable, EmptyState, Pagination, StatusPill, type ColumnDef } from '@vnetwork/ui/data';
import { Section } from '@vnetwork/ui/layout';
import { useResellerServices, type ResellerServiceRow } from '@/api/resellers';
import { EntityLink } from '@/components/entity-link';
import { paginationView } from '@/pages/customers/pagination-summary';
import { TabError, TabLoading } from '@/pages/customers/detail/tab-states';
import { formatDate, formatMoney } from '@/lib/format';

const PAGE_SIZE = 20;

function serviceStatusTone(status: string) {
  switch (status) {
    case 'active':    return 'success' as const;
    case 'pending':   return 'warning' as const;
    case 'suspended': return 'danger'  as const;
    default:          return 'neutral' as const;
  }
}

function humanize(s: string) {
  if (!s) return '—';
  return s.charAt(0).toUpperCase() + s.slice(1).replace(/-/g, ' ');
}

const COLUMNS: ColumnDef<ResellerServiceRow, unknown>[] = [
  {
    id: 'service',
    header: 'Service',
    cell: ({ row }) => (
      <EntityLink
        to={`/services/${row.original.service_id}`}
        id={row.original.service_id}
        name={row.original.service_name}
      />
    ),
  },
  {
    id: 'customer_name',
    header: 'Customer',
    cell: ({ row }) => (
      <EntityLink
        to={`/customers/${row.original.customer_id}`}
        id={row.original.customer_id}
        name={row.original.customer_name}
      />
    ),
  },
  {
    id: 'service_status',
    header: 'Status',
    cell: ({ row }) => (
      <StatusPill tone={serviceStatusTone(row.original.service_status)}>
        {humanize(row.original.service_status)}
      </StatusPill>
    ),
  },
  {
    id: 'price_recurring',
    header: () => <div style={{ textAlign: 'right' }}>Monthly Price</div>,
    cell: ({ row }) => (
      <div style={{ textAlign: 'right' }}>{formatMoney(row.original.price_recurring)}</div>
    ),
  },
  {
    id: 'created_at',
    header: 'Created',
    cell: ({ row }) => formatDate(row.original.created_at),
  },
];

/** Services tab — list of services from reseller's linked customers. */
export function ResellerServicesTab({ resellerId }: { resellerId: number }) {
  const [page, setPage] = useState(1);

  const { data, isLoading, isError, refetch } = useResellerServices(resellerId, {
    page,
    page_size: PAGE_SIZE,
  });

  const rows = data?.results ?? [];
  const total = data?.pagination?.total ?? rows.length;
  const pageCount = data?.pagination?.total_pages ?? 1;

  return (
    <Section title="Services">
      {isError ? (
        <TabError title="Failed to load services" onRetry={() => refetch()} />
      ) : isLoading ? (
        <TabLoading rows={5} />
      ) : (
        <DataTable
          columns={COLUMNS}
          data={rows}
          getRowId={(r) => String(r.service_id)}
          emptyState={
            <EmptyState
              icon={<Layers size={24} />}
              title="No services"
              description="Services from linked customers will appear here."
            />
          }
          footer={(() => {
            const view = paginationView(page, PAGE_SIZE, rows.length, total, pageCount);
            return (
              <Pagination
                page={page}
                pageCount={view.pageCount}
                onPageChange={setPage}
                summary={view.summary}
              />
            );
          })()}
        />
      )}
    </Section>
  );
}
